import { getChatWsUrl } from '../config';
/**
 * Singleton WebSocket Manager
 * Ensures only one WebSocket connection exists at a time
 */

let globalWebSocket: WebSocket | null = null;
let globalPingInterval: ReturnType<typeof setInterval> | null = null;
let globalSessionId: string | null = null;
let connectionCallbacks: Set<(isConnected: boolean) => void> = new Set();
let messageCallbacks: Set<(data: any) => void> = new Set();

const PING_INTERVAL = 30000;

/**
 * Check if a string is a valid UUID
 */
const isValidUUID = (str: string | null): boolean => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const getAccessToken = (): string | null => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('CognitoIdentityServiceProvider') && key.endsWith('.accessToken')) {
        return localStorage.getItem(key);
      }
    }
  } catch (error) {
    console.error('[WebSocketManager] Error getting access token:', error);
  }
  return null;
};

const notifyConnectionChange = (isConnected: boolean) => {
  connectionCallbacks.forEach((callback) => callback(isConnected));
};

const notifyMessage = (data: any) => {
  messageCallbacks.forEach((callback) => callback(data));
};

const startPingInterval = () => {
  if (globalPingInterval) {
    clearInterval(globalPingInterval);
  }

  globalPingInterval = setInterval(() => {
    if (globalWebSocket?.readyState === WebSocket.OPEN) {
      try {
        globalWebSocket.send(
          JSON.stringify({
            type: 'ping',
            timestamp: Date.now(),
          })
        );
        console.log('[WebSocketManager] Sent ping');
      } catch (error) {
        console.error('[WebSocketManager] Error sending ping:', error);
      }
    }
  }, PING_INTERVAL);
};

const stopPingInterval = () => {
  if (globalPingInterval) {
    clearInterval(globalPingInterval);
    globalPingInterval = null;
  }
};

let isConnecting = false;
let lastConnectTime = 0;
const CONNECT_DEBOUNCE_MS = 500; // Prevent rapid reconnections

export const websocketManager = {
  connect: (sessionId: string | null) => {
    const accessToken = getAccessToken();

    if (!accessToken) {
      console.error('[WebSocketManager] No access token');
      return false;
    }

    // Validate session ID is a valid UUID (or null for no session)
    if (sessionId && !isValidUUID(sessionId)) {
      console.error('[WebSocketManager] Invalid session ID format (not a UUID), rejecting connection:', sessionId);
      console.error('[WebSocketManager] Please clear localStorage and create a new session');
      return false;
    }

    // Debounce rapid connection attempts
    const now = Date.now();
    if (now - lastConnectTime < CONNECT_DEBOUNCE_MS && isConnecting) {
      console.log('[WebSocketManager] Connection attempt debounced');
      return false;
    }
    lastConnectTime = now;

    // If already connected with the same session ID, do nothing
    if (globalWebSocket?.readyState === WebSocket.OPEN && globalSessionId === sessionId) {
      console.log('[WebSocketManager] Already connected with session:', sessionId);
      return true;
    }

    // Close existing connection if session ID changed
    if (globalWebSocket && globalSessionId !== sessionId) {
      console.log('[WebSocketManager] Session ID changed, closing old connection');
      globalWebSocket.close(1000, 'Session changed');
      globalWebSocket = null;
    }

    // Don't create new connection if already connecting
    if (globalWebSocket?.readyState === WebSocket.CONNECTING || isConnecting) {
      console.log('[WebSocketManager] Already connecting...');
      return false;
    }

    // Create new connection
    try {
      isConnecting = true;
      globalSessionId = sessionId;
      let url = `${getChatWsUrl()}?token=${encodeURIComponent(accessToken)}`;
      if (sessionId) {
        url += `&session_id=${encodeURIComponent(sessionId)}`;
      }

      console.log('[WebSocketManager] Creating new connection to:', url);
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('[WebSocketManager] WebSocket connected successfully');
        isConnecting = false;
        notifyConnectionChange(true);
        startPingInterval();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle pong silently
          if (data.type === 'pong') {
            console.log('[WebSocketManager] Received pong', data);
            return;
          }

          notifyMessage(data);
        } catch (error) {
          console.error('[WebSocketManager] Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[WebSocketManager] WebSocket error:', error);
        isConnecting = false;
        notifyConnectionChange(false);
        stopPingInterval();
      };

      ws.onclose = (event) => {
        console.log('[WebSocketManager] WebSocket closed:', event.code, event.reason, 'wasClean:', event.wasClean);
        isConnecting = false;
        notifyConnectionChange(false);
        stopPingInterval();
        globalWebSocket = null;

        // Only reconnect on abnormal closure (not normal close codes)
        if (event.code !== 1000 && event.code !== 1001 && !event.wasClean && globalSessionId) {
          console.log('[WebSocketManager] Abnormal closure detected, attempting to reconnect in 3 seconds...');
          setTimeout(() => {
            // Double-check session ID hasn't changed and we're not already connecting
            if (globalSessionId && !isConnecting && !globalWebSocket) {
              websocketManager.connect(globalSessionId);
            }
          }, 3000);
        } else {
          console.log('[WebSocketManager] Normal closure, not reconnecting');
        }
      };

      globalWebSocket = ws;
      return true;
    } catch (error) {
      console.error('[WebSocketManager] Error creating WebSocket:', error);
      isConnecting = false;
      globalSessionId = null;
      return false;
    }
  },

  disconnect: () => {
    stopPingInterval();
    isConnecting = false;
    if (globalWebSocket) {
      globalWebSocket.close(1000, 'Client disconnect');
      globalWebSocket = null;
    }
    globalSessionId = null;
    notifyConnectionChange(false);
  },

  sendMessage: (message: string) => {
    if (globalWebSocket?.readyState === WebSocket.OPEN) {
      globalWebSocket.send(
        JSON.stringify({
          type: 'message',
          message: message,
        })
      );
      return true;
    }
    console.error('[WebSocketManager] Cannot send message - not connected');
    return false;
  },

  isConnected: (): boolean => {
    return globalWebSocket?.readyState === WebSocket.OPEN;
  },

  getSessionId: (): string | null => {
    return globalSessionId;
  },

  onConnectionChange: (callback: (isConnected: boolean) => void) => {
    connectionCallbacks.add(callback);
    return () => {
      connectionCallbacks.delete(callback);
    };
  },

  onMessage: (callback: (data: any) => void) => {
    messageCallbacks.add(callback);
    return () => {
      messageCallbacks.delete(callback);
    };
  },
};
